import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { CarouselArticle } from '../../carousel';
import { isMobile } from '../../../utils/adaptive';
import VideoArticle from './video-article';
import {
  Container,
  Wrapper,
  ListArticle,
  MenuWrapper,
  TabMenu,
  Tab,
  SortMenu,
  FlexWrapper,
  MobileArticle,
  MobileAnchor,
  ReadMore,
} from '../styles/article-list.style';
import { generateFixedSizeUrl } from '../../../utils/webp';

const propTypes = {
  articles: PropTypes.array,
};

const defaultProps = {
  articles: [
    {
      node: {},
    },
  ],
};

/**
 * News Component
 */
class News extends React.Component {
  /**
   * News Component Constructor
   * @param {object} props Incoming Props
   */
  constructor(props) {
    super(props);
    this.changeTab = this.changeTab.bind(this);
    this.changeFilter = this.changeFilter.bind(this);
    this.showMore = this.showMore.bind(this);
    this.state = {
      tab: 'all',
      filter: 'all',
      mobileView: false,
      mounted: false,
      numShown: 3,
      // filter buttons hide states
      showArticlesTab: true,
      showVideosTab: true,
    };
  }

  /**
   * Determine if mobile components should be used
   */
  componentDidMount() {
    if (isMobile()) {
      this.setState({ mobileView: true });
    }
    this.setState({ mounted: true });
  }

  /**
   * Determine if components was updated
   * @param {object} prevProps - prev props
   * @param {object} prevState - prev state
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      filter, tab, showArticlesTab, showVideosTab,
    } = this.state;

    if (tab !== prevState.tab) {
      this.updateFiltersState();
    }

    if ((filter === 'articles' && !showArticlesTab) || (filter === 'videos' && !showVideosTab)) {
      this.changeFilter('all');
    }
  }

  /**
   * @method getButton
   * @description get filter switcher button
   * @param {string} className - html element class name
   * @param {string} filter - filter value
   * @param {string} title - button title
   * @param {boolean} visible - button title
   * @returns {string} button template or empty string if nothing to show
   */
  getButton(className, filter, title, visible) {
    return visible ? (
      <button
        type="button"
        className={className}
        onClick={() => this.changeFilter(filter)}
      >
        {title}
      </button>
    ) : '';
  }

  /**
   * @method getFilteredArticles
   * @description get filtered by selected filter articles
   * @param {object[]} articles - html element class name
   * @param {string} [_filter] - optional article filter, used only to define if we should show target filter button
   * @param {string} [newTab] - pass, if call with changeTab()
   * @returns {object[]} button template or empty string if nothing to show
   */
  getFilteredArticles(articles, _filter, newTab) {
    const { filter, tab } = this.state;
    const currentFilter = _filter || filter;

    const targetTab = newTab || tab;
    let sorted = [];
    for (let i = 0; i < articles.length; i++) {
      sorted.push(articles[i].node);
    }

    if (targetTab !== 'all') {
      sorted = sorted.filter(item => item.tags && item.tags.indexOf(targetTab) > -1);
    }

    if (currentFilter !== 'all') {
      sorted = sorted.filter((item) => {
        if (currentFilter === 'articles') {
          return item.videoStill === null;
        }
        if (currentFilter === 'videos') {
          return item.videoStill !== null;
        }
        return true;
      });
    }

    return sorted;
  }

  /**
   * Update filter buttons state
   * */
  updateFiltersState() {
    const { articles } = this.props;
    this.setState({
      showArticlesTab: this.getFilteredArticles(articles, 'articles').length > 0,
      showVideosTab: this.getFilteredArticles(articles, 'videos').length > 0,
    });
  }

  /**
   * Switch Filter
   * @param {string} filter Name of the tab to switch to
   */
  changeFilter(filter) {
    this.setState({ filter });
  }

  /**
   * Switch Tabs
   * @param {string} tab Name of the tab to switch to
   */
  changeTab(tab) {
    this.setState({ tab });
  }

  /**
   * Show more
   */
  showMore() {
    this.setState({ numShown: 1000 });
  }

  /**
   * Renders a News Component
   * @returns {XML} News Component
   */
  render() {
    const { articles } = this.props;
    const {
      tab, filter, mounted, mobileView, numShown,
    } = this.state;
    const sorted = this.getFilteredArticles(articles);

    let articleList = '';
    let readMore = '';
    if (mobileView && mounted && sorted) {
      articleList = sorted.map((data, index) => {
        let displayDate = moment(data.date)
          .format('MMMM YYYY');
        if (!data.date) {
          displayDate = moment(data.updatedAt)
            .format('Mo YYYY');
        }
        let src = '';
        if (data && data.image && data.image.file) {
          src = generateFixedSizeUrl({
            img: {
              path: data.image.file.url,
              width: data.image.file.details.image.width,
              height: data.image.file.details.image.height,
            },
            dimensions: {
              height: 100,
              width: 100,
            },
            ratio: window.devicePixelRatio,
          });
        }
        let articleSource = '';
        if (data.source) {
          articleSource = data.source;
        }
        if (index < numShown) {
          if (data.videoStill) {
            return <VideoArticle key={index} data={data} isMobile />;
          }
          if (data.externalUrl) {
            return (
              <MobileAnchor key={index} target="blank" href={data.externalUrl}>
                <div className="date">{`${articleSource}${data.source && displayDate ? ' | ' : ''}${displayDate}`}</div>
                <div className="title">{data.title}</div>
                <div className="image" style={{ backgroundImage: `url('${src}')` }} />
              </MobileAnchor>
            );
          }
          return (
            <MobileArticle key={index} to={`/articles/${data.articleSlug}`}>
              <div className="date">{`${articleSource}${data.source && displayDate ? ' | ' : ''}${displayDate}`}</div>
              <div className="title">{data.title}</div>
              <div className="image" style={{ backgroundImage: `url('${src}')` }} />
            </MobileArticle>
          );
        }
        return '';
      });
      if (numShown < sorted.length) {
        readMore = <ReadMore onClick={this.showMore}>More Articles</ReadMore>;
      }
    }
    if (!mobileView && mounted && sorted) {
      articleList = (
        <FlexWrapper>
          {sorted.map((node, index) => {
            if (node.videoStill) {
              return (
                <ListArticle key={index} video>
                  <VideoArticle data={node} />
                </ListArticle>
              );
            }
            return (
              <ListArticle key={index}>
                <CarouselArticle data={node} />
              </ListArticle>
            );
          })}
        </FlexWrapper>
      );
    }

    const { showArticlesTab, showVideosTab } = this.state;
    return (
      <Container>
        <MenuWrapper>
          <TabMenu>
            <Tab active={tab === 'all'} onClick={() => this.changeTab('all')}>
              {'All'}
            </Tab>
            <Tab active={tab === 'Team'} onClick={() => this.changeTab('Team')}>
              {'Teams'}
            </Tab>
            <Tab active={tab === 'League'} onClick={() => this.changeTab('League')}>
              {'League'}
            </Tab>
            <Tab active={tab === 'Game'} onClick={() => this.changeTab('Game')}>
              {'Football'}
            </Tab>
          </TabMenu>
        </MenuWrapper>
        <Wrapper>
          <SortMenu>
            {this.getButton(filter === 'all' ? 'active ' : '', 'all', 'All', true)}
            {showArticlesTab ? '|' : ''}
            {this.getButton(filter === 'articles' ? 'active ' : '', 'articles', 'Latest Articles', showArticlesTab)}
            {showVideosTab ? '|' : ''}
            {this.getButton(filter === 'videos' ? 'active ' : '', 'videos', 'Videos', showVideosTab)}
          </SortMenu>
          {articleList}
          {readMore}
        </Wrapper>
      </Container>
    );
  }
}

News.propTypes = propTypes;
News.defaultProps = defaultProps;

export default News;



// WEBPACK FOOTER //
// ./src/components/article-list/components/article-list.js